import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fadeUp, staggerContainer } from "@/lib/animations";

interface StepDef {
    num: string;
    labelEn: string;
    key: "discover" | "design" | "build" | "launch";
}

const STEPS: StepDef[] = [
    { num: "01", labelEn: "DISCOVER", key: "discover" },
    { num: "02", labelEn: "DESIGN", key: "design" },
    { num: "03", labelEn: "BUILD", key: "build" },
    { num: "04", labelEn: "LAUNCH", key: "launch" },
];

const StepTile = ({ step }: { step: StepDef }) => {
    const { t } = useTranslation();

    return (
        <motion.div
            variants={fadeUp}
            className="group relative border border-white/10 hover:border-white/25 transition-colors duration-300 p-7 sm:p-8 md:p-10 flex flex-col bg-white/[0.015] min-h-[280px]"
        >
            <span
                aria-hidden
                className="absolute top-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />

            <div className="flex items-center justify-between mb-8 sm:mb-10">
                <span className="font-v2-display text-6xl sm:text-7xl md:text-8xl text-white leading-none tabular-nums">
                    {step.num}
                </span>
                <span className="font-mono-brutal text-[10px] text-white/45">
                    {step.labelEn}
                </span>
            </div>

            <h3 className="font-v2-display text-xl sm:text-2xl text-white leading-tight mb-3">
                {t(`process.steps.${step.key}.title`)}
            </h3>

            <p className="text-sm text-white/60 leading-relaxed font-medium">
                {t(`process.steps.${step.key}.desc`)}
            </p>
        </motion.div>
    );
};

const ProcessWithVideos = () => {
    const { t } = useTranslation();

    return (
        <section
            id="process"
            className="py-12 sm:py-16 md:py-24 lg:py-28"
            aria-labelledby="process-heading"
        >
            <div className="container mx-auto px-5 sm:px-6 mb-10 sm:mb-14">
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                    <span aria-hidden className="brutal-glyph" style={{ height: "0.65em" }} />
                    <span className="font-mono-brutal text-[11px] text-white/60">
                        {t("process.eyebrow")}
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-white/10" />
                    <span className="font-mono-brutal text-[10px] text-white/35 tabular-nums">
                        [{String(STEPS.length).padStart(2, "0")} STEPS]
                    </span>
                </div>

                <h2
                    id="process-heading"
                    className="font-v2-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
                >
                    {t("process.title_prefix")}{" "}
                    <span
                        style={{
                            backgroundImage: "var(--gradient-accent-v2)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            color: "transparent",
                        }}
                    >
                        {t("process.title_highlight")}
                    </span>
                </h2>
                <p className="mt-5 sm:mt-6 text-sm sm:text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">
                    {t("process.subtitle")}
                </p>
            </div>

            <div className="container mx-auto px-5 sm:px-6">
                <motion.div
                    variants={staggerContainer(0.1, 0.05)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8"
                >
                    {STEPS.map((step) => (
                        <StepTile key={step.num} step={step} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ProcessWithVideos;
